import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { db } from '../data/store';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, newId } from '@battleops/localstore';
import { makeBlockDoc, commands, blockOn } from '@battleops/shared';
import { useSession } from './SessionContext';

const TimeBlockContext = createContext();
const COLLECTION = 'timeBlocks';

const initialState = {
  blocks: [],
  loaded: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD':
      return { ...state, blocks: action.blocks, loaded: true };
    case 'SET':
      return { ...state, blocks: action.blocks };
    default:
      return state;
  }
}

export function TimeBlockProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const uid = useSession()?.identity?.uid || null;
  // Your day counts only the job days you're on (crew.js): someone else's crew block stays off your plan.
  const mine = useCallback((b) => !uid || blockOn(b, uid), [uid]);

  useEffect(() => {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const blocks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      dispatch({ type: 'LOAD', blocks });
    }, () => {
      dispatch({ type: 'LOAD', blocks: [] });
    });
    return () => unsub();
  }, []);

  // A block is a PROJECT on the calendar (shared/commands makeBlockDoc: projectId required, minutes + legacy
  // hour fields both written, kind work | job-day). Throws without a project.
  const addBlock = useCallback((block) => {
    const doc_ = makeBlockDoc(block, Date.now(), newId);
    dispatch({ type: 'SET', blocks: [...state.blocks, doc_] });
    setDoc(doc(db, COLLECTION, doc_.id), doc_);
    return doc_.id;
  }, [state.blocks]);

  const updateBlock = useCallback((block) => {
    const full = state.blocks.find(b => b.id === block.id);
    if (!full) return;
    let data;
    try { ({ patch: data } = commands.updateBlock({ blocks: state.blocks }, block)); } catch (e) { console.error('updateBlock refused:', e); return; }
    dispatch({ type: 'SET', blocks: state.blocks.map(b => b.id === block.id ? { ...b, ...data } : b) });
    updateDoc(doc(db, COLLECTION, block.id), data);
  }, [state.blocks]);

  const deleteBlock = useCallback((id) => {
    dispatch({ type: 'SET', blocks: state.blocks.filter(b => b.id !== id) });
    deleteDoc(doc(db, COLLECTION, id));
  }, [state.blocks]);

  const myBlocks = React.useMemo(() => state.blocks.filter(mine), [state.blocks, mine]);

  const getBlocksForDate = useCallback((date) => {
    return state.blocks
      .filter(b => b.date === date && mine(b))
      .sort((a, b) => a.startHour * 60 + a.startMinute - (b.startHour * 60 + b.startMinute));
  }, [state.blocks, mine]);

  const getBlocksForRange = useCallback((startDate, endDate) => {
    return state.blocks.filter(b => b.date >= startDate && b.date <= endDate && mine(b));
  }, [state.blocks, mine]);

  return (
    <TimeBlockContext.Provider value={{
      blocks: state.blocks,
      loaded: state.loaded,
      myBlocks,
      addBlock,
      updateBlock,
      deleteBlock,
      getBlocksForDate,
      getBlocksForRange,
    }}>
      {children}
    </TimeBlockContext.Provider>
  );
}

export function useTimeBlocks() {
  const ctx = useContext(TimeBlockContext);
  if (!ctx) throw new Error('useTimeBlocks must be used within TimeBlockProvider');
  return ctx;
}
